import React, { useState, useEffect, useMemo} from 'react';
import BarraFiltros, { SinResultados } from './BarraFiltros';
import {
  createTheme,
  ThemeProvider,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  CircularProgress,
  Typography,
  Box,
} from '@mui/material';
import Swal from 'sweetalert2';
import { styled } from '@mui/system';

// Usar el mismo tema definido en UsuariosPage
const theme = createTheme({
  palette: {
    primary: { main: '#1976d2' },
    secondary: { main: '#dc004e' },
  },
});

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.common.white,
  fontWeight: 'bold',
}));

const Auditoria = () => {
  const [registros, setRegistros] = useState([]);
  const [search, setSearch] = useState('');
  const [usuarioFiltro, setUsuarioFiltro] = useState('');
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAuditoria = async () => {
      try {
        const token = localStorage.getItem('access_token');
        const response = await fetch('https://api.hmsaa.com.mx:5001/api/auditoria', {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (!response.ok) {
          throw new Error('Error al cargar registros de auditoría');
        }
        
        const data = await response.json();
        setRegistros(data);
      } catch (err) {
        console.error('Error:', err);
        Swal.fire('Error', 'No se pudieron cargar los registros de auditoría', 'error');
      } finally {
        setLoading(false);
      }
    };
    
    fetchAuditoria();
  }, []);

  const usuariosRegistrados = useMemo(
    () => [...new Set(registros.map(r => r.usuario).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [registros]
  );

  const filteredRegistros = useMemo(() => {
    const termino = search.trim().toLowerCase();
    const desdeFecha = desde ? new Date(`${desde}T00:00:00`) : null;
    const hastaFecha = hasta ? new Date(`${hasta}T23:59:59.999`) : null;

    return registros.filter(registro => {
      if (usuarioFiltro && registro.usuario !== usuarioFiltro) return false;

      if (desdeFecha || hastaFecha) {
        const fecha = new Date(registro.fecha_hora);
        if (desdeFecha && fecha < desdeFecha) return false;
        if (hastaFecha && fecha > hastaFecha) return false;
      }

      return [registro.accion, registro.detalle, registro.usuario].some(v =>
        String(v ?? '').toLowerCase().includes(termino)
      );
    });
  }, [registros, search, usuarioFiltro, desde, hasta]);

  const limpiarFiltros = () => {
    setSearch('');
    setUsuarioFiltro('');
    setDesde('');
    setHasta('');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ padding: '20px' }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Registros de Auditoría
        </Typography>

        <BarraFiltros
          busqueda={search}
          onBusqueda={setSearch}
          placeholder="Buscar por usuario, acción o detalle"
          filtros={[
            {
              id: 'usuario',
              tipo: 'select',
              etiqueta: 'Usuario',
              textoTodos: 'Todos los usuarios',
              valor: usuarioFiltro,
              onChange: setUsuarioFiltro,
              opciones: usuariosRegistrados.map(u => ({ valor: u, etiqueta: u })),
            },
            { id: 'desde', tipo: 'fecha', etiqueta: 'Desde', valor: desde, onChange: setDesde },
            { id: 'hasta', tipo: 'fecha', etiqueta: 'Hasta', valor: hasta, onChange: setHasta },
          ]}
          total={registros.length}
          mostrados={filteredRegistros.length}
          onLimpiar={limpiarFiltros}
          sx={{ borderRadius: 2, border: '1px solid #e3e8ef', mb: 2 }}
        />

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <StyledTableCell>ID</StyledTableCell>
                <StyledTableCell>Usuario</StyledTableCell>
                <StyledTableCell>Acción</StyledTableCell>
                <StyledTableCell>Detalle</StyledTableCell>
                <StyledTableCell>Fecha y Hora</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRegistros.map((registro) => (
                <TableRow key={registro.id}>
                  <TableCell>{registro.id}</TableCell>
                  { <TableCell>{registro.usuario/*.nombre*/}</TableCell> }
                  <TableCell>{registro.accion}</TableCell>
                  <TableCell>{JSON.stringify(registro.detalle)}</TableCell>
                  <TableCell>{new Date(registro.fecha_hora).toLocaleString()}</TableCell>
                </TableRow>
              ))}
              {filteredRegistros.length === 0 && (
                <SinResultados colSpan={5} total={registros.length} onLimpiar={limpiarFiltros} entidad="registros de auditoría" />
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </ThemeProvider>
  );
};

export default Auditoria;